import { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';
import StatusPill from '../components/StatusPill';
import VideoUploader from '../components/VideoUploader';
import ShareLinkManager from '../components/ShareLinkManager';

const FORMAT_COLS = {
  Skit:         [{ key: 'character', label: 'Character' }, { key: 'line', label: 'Line' }, { key: 'notes', label: 'Notes' }, { key: 'scene', label: 'Scene' }, { key: 'props', label: 'Props' }],
  Longform:     [{ key: 'sceneNumber', label: 'Scene #' }, { key: 'character', label: 'Character' }, { key: 'line', label: 'Line' }, { key: 'scene', label: 'Scene Description' }],
  TH:           [{ key: 'narration', label: 'Narration' }, { key: 'aRoll', label: 'A Roll' }, { key: 'bRoll', label: 'B Roll' }, { key: 'vfx', label: 'VFX' }, { key: 'sfx', label: 'SFX' }, { key: 'props', label: 'Props' }],
  AI:           [{ key: 'narration', label: 'Narration' }, { key: 'visuals', label: 'Visuals' }],
  Mockumentary: [{ key: 'character', label: 'Character' }, { key: 'line', label: 'Line' }, { key: 'notes', label: 'Notes' }, { key: 'aroll', label: 'A-Roll' }, { key: 'broll', label: 'B-Roll' }, { key: 'vfx', label: 'FX' }, { key: 'props', label: 'Props' }, { key: 'location', label: 'Location' }],
};

function ScriptBodyRenderer({ scriptBody }) {
  if (!scriptBody) return <span style={{ color: 'var(--tx-faint)', fontStyle: 'italic', fontFamily: 'var(--font)' }}>No script content attached.</span>;
  if (typeof scriptBody === 'string') return scriptBody;
  const { format, scenes = [] } = scriptBody;
  const cols = FORMAT_COLS[format] || Object.keys(scenes[0] || {}).map(k => ({ key: k, label: k }));
  if (!scenes.length) return <span style={{ color: 'var(--tx-faint)', fontStyle: 'italic', fontFamily: 'var(--font)' }}>Script has no scenes yet.</span>;
  return (
    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
      <thead>
        <tr style={{ background: 'var(--bg-card2)' }}>
          <th style={{ width: 32, padding: '6px 8px', textAlign: 'left', color: 'var(--tx-muted)', fontWeight: 700, fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.06em', borderBottom: '1px solid var(--border)' }}>#</th>
          {cols.map(col => (
            <th key={col.key} style={{ padding: '6px 8px', textAlign: 'left', color: 'var(--tx-muted)', fontWeight: 700, fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.06em', borderBottom: '1px solid var(--border)' }}>{col.label}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {scenes.map((scene, i) => (
          <tr key={i} style={{ borderBottom: '1px solid var(--border)', verticalAlign: 'top' }}>
            <td style={{ padding: '8px', color: 'var(--tx-faint)', fontSize: 11, whiteSpace: 'nowrap' }}>{i + 1}</td>
            {cols.map(col => (
              <td key={col.key} style={{ padding: '8px', color: 'var(--tx-secondary)', lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>
                {scene[col.key] || ''}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function fmtDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

const TRACK_PILL = {
  Live: { bg: 'var(--violet-soft)',   color: 'var(--violet)', label: 'Live' },
  AI:   { bg: 'rgba(77,170,112,0.12)', color: '#4daa70',       label: 'AI'   },
};

function TrackBadge({ track }) {
  const s = TRACK_PILL[track] || TRACK_PILL.Live;
  return (
    <span style={{
      background: s.bg, color: s.color,
      fontSize: 10, fontWeight: 700, padding: '2px 7px',
      borderRadius: 4, letterSpacing: '0.03em', textTransform: 'uppercase',
    }}>{s.label}</span>
  );
}

// ── Script reader panel ───────────────────────────────────────────────────────
function autoResizeQst(el) {
  el.style.height = '0px';
  el.style.height = el.scrollHeight + 'px';
}

function QualScriptPanel({ scriptId, canEdit, onClose, onStatusChanged }) {
  const navigate = useNavigate();
  const [script,        setScript]        = useState(null);
  const [loading,       setLoading]       = useState(true);
  const [qualNotes,     setQualNotes]     = useState('');
  const [draftScenes,   setDraftScenes]   = useState(null);
  const [dirty,         setDirty]         = useState(false);
  const [saving,        setSaving]        = useState(false);
  const [transitioning, setTransitioning] = useState(false);

  useEffect(() => {
    setLoading(true);
    api.get(`/api/scripts/${scriptId}`)
      .then(r => {
        setScript(r.data);
        setQualNotes(r.data.qualNotes || '');
        setDraftScenes(r.data.scriptBody?.scenes ? r.data.scriptBody.scenes.map(s => ({ ...s })) : null);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [scriptId]);

  // Resize all editable cells once the script finishes loading and renders
  useEffect(() => {
    if (loading || !canEdit) return;
    let raf2;
    const raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => {
        document.querySelectorAll('.qst-edit-input').forEach(autoResizeQst);
      });
    });
    return () => { cancelAnimationFrame(raf1); cancelAnimationFrame(raf2); };
  }, [loading, canEdit]);

  const handleSceneNoteChange = (i, value) => {
    setDraftScenes(prev => {
      const next = [...prev];
      next[i] = { ...next[i], qualNotes: value };
      return next;
    });
    setDirty(true);
  };

  const handleSceneCellChange = (i, col, value) => {
    setDraftScenes(prev => {
      const next = [...prev];
      next[i] = { ...next[i], [col]: value };
      return next;
    });
    setDirty(true);
  };

  const buildPayload = () => {
    const payload = { qualNotes };
    if (draftScenes && script?.scriptBody) {
      payload.scriptBody = { ...script.scriptBody, scenes: draftScenes };
    }
    return payload;
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.patch(`/api/scripts/${scriptId}`, buildPayload());
      setDirty(false);
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to save');
    } finally { setSaving(false); }
  };

  const handleStatusChange = async (newStatus) => {
    setTransitioning(true);
    try {
      await api.patch(`/api/scripts/${scriptId}`, buildPayload());
      await api.patch(`/api/scripts/${scriptId}/status`, { status: newStatus });
      onStatusChanged(scriptId);
      onClose();
    } catch (err) {
      alert(err.response?.data?.error || 'Status update failed');
      setTransitioning(false);
    }
  };

  const format = script?.scriptBody?.format;
  const cols   = FORMAT_COLS[format] || [];

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="qual-panel qual-panel-wide" onClick={e => e.stopPropagation()}>
        <div className="qual-panel-header">
          <div style={{ minWidth: 0 }}>
            <div className="vc-label">Script · Qual Review</div>
            {script ? (
              <>
                <div className="vc-title" style={{ marginTop: 4 }}>{script.scriptName}</div>
                <div style={{ marginTop: 6, display: 'flex', gap: 10, fontSize: 12, color: 'var(--tx-muted)', flexWrap: 'wrap' }}>
                  <span>{script.client?.name}</span>
                  <span>·</span>
                  <span>{script.writer?.name}</span>
                  <span>·</span>
                  <span>{fmtDate(script.dateEntered)}</span>
                  {script.qualReviewer && <><span>·</span><span>Reviewer: {script.qualReviewer.name}</span></>}
                </div>
              </>
            ) : loading ? (
              <div style={{ color: 'var(--tx-muted)', marginTop: 6 }}>Loading…</div>
            ) : null}
          </div>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, flexShrink: 0 }}>
            {canEdit && script && (
              <button
                className="btn-ghost"
                style={{ fontSize: 12, flexShrink: 0 }}
                onClick={() => { onClose(); navigate(`/scripts?script=${scriptId}`); }}
              >
                Open in Editor ↗
              </button>
            )}
            <button className="vc-close" onClick={onClose}>✕</button>
          </div>
        </div>

        {loading ? (
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--tx-muted)', padding: 40 }}>
            Loading script…
          </div>
        ) : !script ? (
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--tx-muted)', padding: 40 }}>
            Script not found.
          </div>
        ) : (
          <div className="qual-panel-body">
            {/* Overall qual notes */}
            <div className="vc-field full">
              <label>
                Summary Note
                <span style={{ fontWeight: 400, marginLeft: 6, fontSize: 10, color: 'var(--tx-muted)', textTransform: 'none', letterSpacing: 0 }}>
                  — shown as a banner in the script editor. For scene-specific corrections, use the Qual Notes column below.
                </span>
              </label>
              <textarea
                className="vc-input"
                value={qualNotes}
                onChange={e => { setQualNotes(e.target.value); setDirty(true); }}
                onBlur={() => { if (dirty) handleSave(); }}
                placeholder="Overall feedback for the writer…"
                readOnly={!canEdit}
                style={{ minHeight: 52 }}
              />
            </div>

            {/* Script table with per-scene qual notes column */}
            {draftScenes ? (
              <div style={{ overflowX: 'auto' }}>
                <table className="qual-script-table">
                  <thead>
                    <tr>
                      <th style={{ width: 32 }}>#</th>
                      {cols.map(col => <th key={col.key}>{col.label}</th>)}
                      <th style={{ minWidth: 180 }}>Qual Notes <span style={{ fontWeight: 400, fontSize: 10, color: 'var(--tx-faint)' }}>(per scene → appears in editor)</span></th>
                    </tr>
                  </thead>
                  <tbody>
                    {draftScenes.map((scene, i) => (
                      <tr key={i}>
                        <td className="qst-num">{i + 1}</td>
                        {cols.map(col => (
                          <td key={col.key} className={`qst-cell${canEdit ? ' qst-cell-edit' : ''}`}>
                            {canEdit ? (
                              <textarea
                                className="qst-edit-input"
                                value={scene[col.key] || ''}
                                onChange={e => handleSceneCellChange(i, col.key, e.target.value)}
                                onInput={e => autoResizeQst(e.target)}
                                rows={1}
                              />
                            ) : (
                              scene[col.key] || ''
                            )}
                          </td>
                        ))}
                        <td className="qst-notes-cell">
                          {canEdit ? (
                            <textarea
                              className="qst-notes-input"
                              value={scene.qualNotes || ''}
                              onChange={e => handleSceneNoteChange(i, e.target.value)}
                              onBlur={() => { if (dirty) handleSave(); }}
                              placeholder="Note…"
                              rows={2}
                            />
                          ) : (
                            <span style={{ color: 'var(--tx-muted)', fontSize: 12 }}>{scene.qualNotes || ''}</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div style={{ color: 'var(--tx-faint)', fontStyle: 'italic', fontSize: 13 }}>No script content attached.</div>
            )}
          </div>
        )}

        <div className="vc-footer">
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {canEdit && !loading && script && (
              <>
                <button className="btn-primary" onClick={() => handleStatusChange('Approved')} disabled={transitioning}>Approve ✓</button>
                <button className="btn-ghost" onClick={() => handleStatusChange('Backburner')} disabled={transitioning}>Backburner</button>
                <button
                  className="btn-ghost"
                  style={{ color: 'var(--s-incomplete-t)', borderColor: 'rgba(212,85,85,0.35)' }}
                  onClick={() => handleStatusChange('Needs Revision')}
                  disabled={transitioning}
                >
                  Needs Revision
                </button>
              </>
            )}
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            {canEdit && dirty && (
              <button className="btn-ghost" onClick={handleSave} disabled={saving}>
                {saving ? 'Saving…' : 'Save Changes'}
              </button>
            )}
            <button className="btn-ghost" onClick={onClose}>Close</button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Video review panel ────────────────────────────────────────────────────────
function QualVideoPanel({ video, canEdit, userId, onClose, onStatusChanged }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [transitioning,      setTransitioning]      = useState(false);
  const [videoAsset,         setVideoAsset]         = useState(null);
  const [assetLoading,       setAssetLoading]       = useState(true);
  const [archivedVersions,   setArchivedVersions]   = useState([]);
  const [deleteVideoConfirm, setDeleteVideoConfirm] = useState(false);
  const [legacyLinkOpen,     setLegacyLinkOpen]     = useState(false);
  const [zipJob,             setZipJob]             = useState(null);
  const zipPollRef = useRef(null);

  const isAdmin     = user?.roles?.some(r => ['Admin', 'Power User'].includes(r));
  const isShootLead = user?.roles?.some(r => ['Producer'].includes(r));
  const isEditor    = user?.roles?.includes('Editor');
  const canDelete   = isAdmin || isShootLead;
  const canUpload   = isAdmin || isShootLead || isEditor;

  const prodParam = video.track === 'Live'
    ? `liveProductionId=${video.id}`
    : `aiProductionId=${video.id}`;

  useEffect(() => {
    api.get(`/api/video-assets?${prodParam}`)
      .then(r => setVideoAsset(r.data))
      .catch(() => {})
      .finally(() => setAssetLoading(false));
    api.get(`/api/video-assets/archived?${prodParam}`)
      .then(r => setArchivedVersions(r.data))
      .catch(() => {});
  }, [prodParam]);

  useEffect(() => {
    return () => { if (zipPollRef.current) clearInterval(zipPollRef.current); };
  }, []);

  const handleStatusChange = async (newStatus) => {
    setTransitioning(true);
    const endpoint = video.track === 'Live'
      ? `/api/live-production/${video.id}/status`
      : `/api/ai-production/${video.id}/status`;
    try {
      await api.patch(endpoint, { status: newStatus, changedBy: userId });
      onStatusChanged(video.id);
      onClose();
    } catch (err) {
      alert(err.response?.data?.error || 'Status update failed');
      setTransitioning(false);
    }
  };

  async function handleDownload() {
    try {
      const res = await api.get(`/api/video-assets/${videoAsset.id}/download`);
      window.open(res.data.url, '_blank');
    } catch {
      alert('Download failed. Please try again.');
    }
  }

  async function handleZipDownload() {
    try {
      setZipJob({ status: 'processing' });
      const res = await api.post('/api/downloads/zip', { videoAssetIds: [videoAsset.id] });
      const { jobId } = res.data;
      zipPollRef.current = setInterval(async () => {
        try {
          const poll = await api.get(`/api/downloads/zip/${jobId}`);
          if (poll.data.status === 'complete') {
            clearInterval(zipPollRef.current);
            setZipJob({ status: 'complete', downloadUrl: poll.data.downloadUrl });
          } else if (poll.data.status === 'failed') {
            clearInterval(zipPollRef.current);
            setZipJob(null);
            alert('Zip generation failed. Please try again.');
          }
        } catch {
          clearInterval(zipPollRef.current);
          setZipJob(null);
        }
      }, 3000);
    } catch {
      setZipJob(null);
      alert('Failed to start zip download. Please try again.');
    }
  }

  async function handleDeleteVideo() {
    try {
      const res = await api.delete(`/api/video-assets/${videoAsset.id}`);
      setDeleteVideoConfirm(false);
      setVideoAsset(null);
      if (res.data.archived) {
        api.get(`/api/video-assets/archived?${prodParam}`)
          .then(r => setArchivedVersions(r.data))
          .catch(() => {});
      }
    } catch (err) {
      setDeleteVideoConfirm(false);
      alert(err.response?.data?.error || 'Failed to delete video.');
    }
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="qual-panel qual-panel-wide" style={{ maxWidth: 680 }} onClick={e => e.stopPropagation()}>
        <div className="qual-panel-header">
          <div style={{ minWidth: 0 }}>
            <div className="vc-label">Video · Qual Review</div>
            <div className="vc-title" style={{ marginTop: 4 }}>{video.name}</div>
            <div style={{ marginTop: 6, display: 'flex', gap: 10, fontSize: 12, color: 'var(--tx-muted)', flexWrap: 'wrap', alignItems: 'center' }}>
              <span>{video.client?.name}</span>
              <span>·</span>
              <TrackBadge track={video.track} />
              {video.editor?.name && <><span>·</span><span>{video.editor.name}</span></>}
            </div>
          </div>
          <button className="vc-close" onClick={onClose}>✕</button>
        </div>

        <div className="qual-panel-body">
          {/* Video thumbnail */}
          <div style={{ marginBottom: 12 }}>
            <label style={{ fontSize: 11, color: 'var(--tx-muted)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', display: 'block', marginBottom: 8 }}>Video</label>
            {assetLoading ? (
              <div style={{ fontSize: 12, color: 'var(--tx-faint)' }}>Loading…</div>
            ) : videoAsset ? (
              <div
                onClick={() => {
                  const p = new URLSearchParams(location.search);
                  p.set('video', video.id);
                  const returnTo = location.pathname + '?' + p.toString();
                  onClose();
                  navigate(`/review/${videoAsset.id}`, { state: { returnTo, returnLabel: 'Video Qual Review', returnVideo: video } });
                }}
                style={{ position: 'relative', width: '100%', maxWidth: 480, borderRadius: 10, overflow: 'hidden', background: '#000', aspectRatio: '16/9', cursor: 'pointer' }}
              >
                {videoAsset.thumbnailUrl ? (
                  <img src={videoAsset.thumbnailUrl} alt="Thumbnail" style={{ width: '100%', height: '100%', objectFit: 'contain', background: '#000', display: 'block' }} />
                ) : (
                  <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--bg-card2)' }}>
                    <span style={{ fontSize: 11, color: 'var(--tx-faint)' }}>Thumbnail generating…</span>
                  </div>
                )}
                <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}>
                  <div style={{ width: 52, height: 52, borderRadius: '50%', background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <div style={{ width: 0, height: 0, borderStyle: 'solid', borderWidth: '10px 0 10px 18px', borderColor: 'transparent transparent transparent #fff', marginLeft: 4 }} />
                  </div>
                </div>
                {(videoAsset.openCommentCount ?? 0) > 0 && (
                  <div style={{ position: 'absolute', bottom: 8, right: 8, background: 'rgba(0,0,0,0.65)', borderRadius: 4, padding: '2px 7px', fontSize: 10, color: '#fff' }}>
                    {videoAsset.openCommentCount} open
                  </div>
                )}
                <div style={{ position: 'absolute', top: 8, left: 8, background: 'rgba(0,0,0,0.65)', borderRadius: 4, padding: '2px 7px', fontSize: 10, color: '#fff', maxWidth: '70%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {videoAsset.fileName}
                </div>
              </div>
            ) : canUpload ? (
              <VideoUploader
                clientId={video.client?.id}
                liveProductionId={video.track === 'Live' ? video.id : undefined}
                aiProductionId={video.track === 'AI' ? video.id : undefined}
                onUploadComplete={asset => setVideoAsset(asset)}
              />
            ) : (
              <div style={{ fontSize: 12, color: 'var(--tx-faint)' }}>No video uploaded yet.</div>
            )}
          </div>

          {/* Action row */}
          {videoAsset && (
            <div style={{ display: 'flex', flexDirection: 'row', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 12 }}>
              <ShareLinkManager videoAssetId={videoAsset.id} />
              <button className="btn-ghost" style={{ fontSize: 12 }} onClick={handleDownload}>
                ↓ Download Video
              </button>
              {zipJob?.status !== 'complete' && (
                <button
                  className="btn-ghost"
                  style={{ fontSize: 12 }}
                  disabled={zipJob?.status === 'processing'}
                  onClick={handleZipDownload}
                >
                  {zipJob?.status === 'processing' ? 'Preparing zip…' : '⬇ Download as Zip'}
                </button>
              )}
              {zipJob?.status === 'complete' && zipJob.downloadUrl && (
                <a href={zipJob.downloadUrl} target="_blank" rel="noreferrer" className="btn-primary"
                  style={{ fontSize: 12, textDecoration: 'none', padding: '8px 14px', borderRadius: 'var(--radius-sm)' }}>
                  ↓ Download Zip
                </a>
              )}
              {(canDelete || videoAsset.uploadedBy === user?.id) && (
                <button
                  className="btn-ghost"
                  style={{ fontSize: 12, color: 'var(--s-incomplete-t)', borderColor: 'rgba(212,85,85,0.35)' }}
                  onClick={() => setDeleteVideoConfirm(true)}
                >
                  Archive Video
                </button>
              )}
            </div>
          )}

          {/* Legacy Frame.io link */}
          {video.frameLink ? (
            <div>
              {videoAsset ? (
                <>
                  <button
                    style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 12, color: 'var(--tx-faint)', padding: 0, fontFamily: 'var(--font)', display: 'flex', alignItems: 'center', gap: 5, marginBottom: legacyLinkOpen ? 8 : 0 }}
                    onClick={() => setLegacyLinkOpen(v => !v)}
                  >
                    <span>{legacyLinkOpen ? '▾' : '▸'}</span> Legacy Frame.io link
                  </button>
                  {legacyLinkOpen && (
                    <a href={video.frameLink} target="_blank" rel="noreferrer"
                      style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '9px 18px', borderRadius: 'var(--radius-sm)', background: 'var(--violet)', color: '#fff', fontSize: 13, fontWeight: 600, textDecoration: 'none' }}>
                      Review in Frame.io ↗
                    </a>
                  )}
                </>
              ) : (
                <a href={video.frameLink} target="_blank" rel="noreferrer"
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '12px 24px', background: 'var(--violet)', color: '#fff', borderRadius: 'var(--radius-sm)', fontWeight: 600, fontSize: 14, textDecoration: 'none' }}>
                  Review in Frame.io ↗
                </a>
              )}
            </div>
          ) : !videoAsset && !assetLoading ? (
            <p style={{ color: 'var(--tx-faint)', fontSize: 13, margin: 0 }}>
              No Frame.io review link attached to this video.
            </p>
          ) : null}
        </div>

        <div className="vc-footer">
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
            {canEdit && (
              <>
                <button className="btn-primary" onClick={() => handleStatusChange('Approved')} disabled={transitioning}>
                  Approve ✓
                </button>
                <button
                  className="btn-ghost"
                  style={{ color: 'var(--tx-secondary)', borderStyle: 'dashed' }}
                  onClick={() => handleStatusChange('Editing')}
                  disabled={transitioning}
                >
                  ← Back to Editing
                </button>
              </>
            )}
            {archivedVersions.length > 0 && (
              <>
                <span style={{ color: 'var(--border)', fontSize: 16, lineHeight: 1 }}>|</span>
                <button
                  className="btn-ghost"
                  style={{ fontSize: 12 }}
                  onClick={() => {
                    const p = new URLSearchParams(location.search);
                    p.set('video', video.id);
                    const returnTo = location.pathname + '?' + p.toString();
                    onClose();
                    navigate(`/review/${archivedVersions[0].id}`, { state: { returnTo, returnLabel: 'Video Qual Review', returnVideo: video } });
                  }}
                >
                  {archivedVersions.length === 1 ? '📁 1 archived version' : `📁 ${archivedVersions.length} archived versions`}
                </button>
              </>
            )}
          </div>
          <button className="btn-ghost" onClick={onClose}>Close</button>
        </div>
      </div>

      {/* R-2 (Context.HeliconflowReviewUploadRestore.md): removing a video always archives it */}
      {deleteVideoConfirm && (() => {
        return (
          <div className="modal-overlay" onClick={() => setDeleteVideoConfirm(false)}>
            <div className="vc-modal" style={{ width: 400 }} onClick={e => e.stopPropagation()}>
              <div className="vc-header">
                <div><div className="vc-title">Archive Video?</div></div>
                <button className="vc-close" onClick={() => setDeleteVideoConfirm(false)}>✕</button>
              </div>
              <div style={{ padding: '20px 28px' }}>
                <p style={{ fontSize: 13, color: 'var(--tx-secondary)', lineHeight: 1.6, margin: 0 }}>
                  <strong style={{ color: 'var(--tx-primary)' }}>{videoAsset?.fileName}</strong> will be <strong style={{ color: 'var(--tx-secondary)' }}>archived</strong> — never deleted from storage.
                </p>
                <p style={{ fontSize: 12, color: 'var(--tx-faint)', lineHeight: 1.6, margin: '10px 0 0' }}>
                  The file and any comments are kept. Archived versions are listed in the Video Review page's version menu, where they can be restored. Only an Admin can permanently delete an archive.
                </p>
              </div>
              <div className="vc-footer">
                <div />
                <div style={{ display: 'flex', gap: 8 }}>
                  <button className="btn-ghost" onClick={() => setDeleteVideoConfirm(false)}>Cancel</button>
                  <button
                    onClick={handleDeleteVideo}
                    style={{ background: 'rgba(212,85,85,0.15)', color: 'var(--s-incomplete-t)', border: '1px solid rgba(212,85,85,0.35)', borderRadius: 'var(--radius-sm)', padding: '9px 18px', fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'var(--font)' }}
                  >
                    Archive Video
                  </button>
                </div>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
}

// ── Main Qual page ────────────────────────────────────────────────────────────
export default function Qual() {
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const { user } = useAuth();

  const tab    = searchParams.get('tab') || 'scripts';
  // Preserve other params (filters, open-video, etc.) when switching tabs.
  const setTab = (t) => setSearchParams(prev => {
    const p = new URLSearchParams(prev);
    p.set('tab', t);
    return p;
  }, { replace: true });

  const canEdit = user?.roles?.some(r => ['Admin', 'Power User', 'Qual Reviewer'].includes(r));

  // ── Scripts tab state ──
  // Filter values seed from the URL so the back-from-player round trip
  // (and a browser refresh) restores whatever the user had selected.
  const [scripts,         setScripts]         = useState([]);
  const [scriptsLoading,  setScriptsLoading]  = useState(false);
  const [sClientFilter,   setSClientFilter]   = useState(() => searchParams.get('sClient')   || '');
  const [sWriterFilter,   setSWriterFilter]   = useState(() => searchParams.get('sWriter')   || '');
  const [sReviewerFilter, setSReviewerFilter] = useState(() => searchParams.get('sReviewer') || '');

  // ── Videos tab state ──
  const [videos,        setVideos]        = useState([]);
  const [videosLoading, setVideosLoading] = useState(false);
  const [vClientFilter, setVClientFilter] = useState(() => searchParams.get('vClient') || '');
  const [vTrackFilter,  setVTrackFilter]  = useState(() => searchParams.get('vTrack')  || '');
  const [vEditorFilter, setVEditorFilter] = useState(() => searchParams.get('vEditor') || '');

  // Push filter changes back into the URL so any returnTo built downstream
  // (QualVideoPanel → /review → back) captures the active filter set.
  useEffect(() => {
    setSearchParams(prev => {
      const p = new URLSearchParams(prev);
      if (sClientFilter)   p.set('sClient',   sClientFilter);   else p.delete('sClient');
      if (sWriterFilter)   p.set('sWriter',   sWriterFilter);   else p.delete('sWriter');
      if (sReviewerFilter) p.set('sReviewer', sReviewerFilter); else p.delete('sReviewer');
      if (vClientFilter)   p.set('vClient',   vClientFilter);   else p.delete('vClient');
      if (vTrackFilter)    p.set('vTrack',    vTrackFilter);    else p.delete('vTrack');
      if (vEditorFilter)   p.set('vEditor',   vEditorFilter);   else p.delete('vEditor');
      return p;
    }, { replace: true });
  }, [sClientFilter, sWriterFilter, sReviewerFilter, vClientFilter, vTrackFilter, vEditorFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Inline panel state ──
  const [openScriptId, setOpenScriptId] = useState(null);
  const [openVideo,    setOpenVideo]    = useState(null);

  // ── Dropdown option lists ──
  const [clients, setClients] = useState([]);
  const [members, setMembers] = useState([]);

  useEffect(() => {
    api.get('/api/clients').then(r => setClients(r.data.filter(c => c.isActive))).catch(() => {});
    api.get('/api/team-members').then(r => setMembers(r.data)).catch(() => {});
  }, []);

  const fetchScripts = useCallback(async () => {
    setScriptsLoading(true);
    const params = new URLSearchParams();
    if (sClientFilter)   params.set('clientId',   sClientFilter);
    if (sWriterFilter)   params.set('writerId',   sWriterFilter);
    if (sReviewerFilter) params.set('reviewerId', sReviewerFilter);
    try {
      const r = await api.get(`/api/qual/scripts?${params}`);
      setScripts(r.data);
    } catch {} finally { setScriptsLoading(false); }
  }, [sClientFilter, sWriterFilter, sReviewerFilter]);

  const fetchVideos = useCallback(async () => {
    setVideosLoading(true);
    const params = new URLSearchParams();
    if (vClientFilter) params.set('clientId', vClientFilter);
    if (vTrackFilter)  params.set('track',    vTrackFilter);
    if (vEditorFilter) params.set('editorId', vEditorFilter);
    try {
      const r = await api.get(`/api/qual/videos?${params}`);
      setVideos(r.data);
    } catch {} finally { setVideosLoading(false); }
  }, [vClientFilter, vTrackFilter, vEditorFilter]);

  useEffect(() => { if (tab === 'scripts') fetchScripts(); }, [tab, fetchScripts]);
  useEffect(() => { if (tab === 'videos')  fetchVideos();  }, [tab, fetchVideos]);

  // Reopen the qual video panel after returning from the player.
  // Preferred path: location.state.openVideo carries the full record for instant render.
  // Fallback: ?video=<id> URL param (used on refresh or direct link).
  const consumedReturnRef = useRef(false);
  useEffect(() => {
    if (consumedReturnRef.current) return;

    if (location.state?.openVideo) {
      setOpenVideo(location.state.openVideo);
      consumedReturnRef.current = true;
      const p = new URLSearchParams(searchParams);
      if (p.has('video')) {
        p.delete('video');
        setSearchParams(p, { replace: true });
      }
      return;
    }

    const openVideoId = searchParams.get('video');
    if (!openVideoId || videos.length === 0) return;
    const v = videos.find(x => x.id === openVideoId);
    if (v) {
      setOpenVideo(v);
      consumedReturnRef.current = true;
    }
    const p = new URLSearchParams(searchParams);
    p.delete('video');
    setSearchParams(p, { replace: true });
  }, [videos, searchParams, setSearchParams, location.state]);

  const filterSelect = (value, onChange, opts, placeholder) => (
    <select className="qual-filter-select" value={value} onChange={e => onChange(e.target.value)}>
      <option value="">{placeholder}</option>
      {opts.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
  );

  return (
    <div className="main">
      <div className="topbar">
        <div>
          <div className="page-title">Qual</div>
          <div className="page-sub">Scripts and videos awaiting quality review</div>
        </div>
      </div>

      <div className="body">

        {/* Tabs */}
        <div className="qual-tabs">
          <button
            className={`qual-tab${tab === 'scripts' ? ' active' : ''}`}
            onClick={() => setTab('scripts')}
          >
            Scripts
            {scripts.length > 0 && tab === 'scripts' && (
              <span className="qual-tab-count">{scripts.length}</span>
            )}
          </button>
          <button
            className={`qual-tab${tab === 'videos' ? ' active' : ''}`}
            onClick={() => setTab('videos')}
          >
            Videos
            {videos.length > 0 && tab === 'videos' && (
              <span className="qual-tab-count">{videos.length}</span>
            )}
          </button>
        </div>

        {/* ── SCRIPTS TAB ── */}
        {tab === 'scripts' && (
          <div className="card" style={{ padding: 0 }}>
            <div className="qual-filters">
              {filterSelect(sClientFilter, setSClientFilter,
                clients.map(c => ({ value: c.id, label: c.name })), 'All Projects')}
              {filterSelect(sWriterFilter, setSWriterFilter,
                members.filter(m => {
                  const roles = m.roles || [];
                  return roles.some(r => (r.role || r) === 'Writer');
                }).map(m => ({ value: m.id, label: m.name })), 'All Writers')}
              {filterSelect(sReviewerFilter, setSReviewerFilter, [
                { value: 'unassigned', label: 'Unassigned' },
                ...members.map(m => ({ value: m.id, label: m.name })),
              ], 'All Reviewers')}
            </div>

            <div className="qual-table">
              <div className="qual-thead">
                <div className="qual-th" style={{ flex: 3 }}>Script</div>
                <div className="qual-th" style={{ flex: 2 }}>Project</div>
                <div className="qual-th" style={{ flex: 2 }}>Writer</div>
                <div className="qual-th" style={{ flex: 1.5 }}>Date Entered</div>
                <div className="qual-th" style={{ flex: 3 }}>Qual Notes</div>
                <div className="qual-th" style={{ flex: 2 }}>Reviewer</div>
                <div className="qual-th" style={{ flex: 2 }}>Status</div>
              </div>

              {scriptsLoading && <div className="qual-empty">Loading…</div>}

              {!scriptsLoading && scripts.length === 0 && (
                <div className="qual-empty">
                  <span style={{ fontSize: 20, marginBottom: 6 }}>✓</span>
                  No scripts in qual. Nothing to review.
                </div>
              )}

              {!scriptsLoading && scripts.map(s => (
                <div key={s.id} className="qual-row">
                  <div
                    className="qual-td qual-link"
                    style={{ flex: 3 }}
                    onClick={() => setOpenScriptId(s.id)}
                    title={s.scriptName}
                  >
                    {s.scriptName}
                    {s.isCookingPhase2 && (
                      <span
                        title="Cooking — Phase 2 (Talking Head built on Phase 1 footage)"
                        style={{
                          marginLeft: 8, display: 'inline-flex', alignItems: 'center',
                          padding: '1px 6px', borderRadius: 3,
                          background: 'rgba(255,160,50,0.12)',
                          border: '1px solid rgba(255,160,50,0.35)',
                          color: 'rgba(255,160,50,1)',
                          fontSize: 9, fontWeight: 700, letterSpacing: '0.04em',
                        }}
                      >
                        🍳 PHASE 2
                      </span>
                    )}
                  </div>
                  <div className="qual-td" style={{ flex: 2 }}>{s.client?.name}</div>
                  <div className="qual-td" style={{ flex: 2 }}>{s.writer?.name}</div>
                  <div className="qual-td" style={{ flex: 1.5 }}>{fmtDate(s.dateEntered)}</div>
                  <div className="qual-td qual-notes" style={{ flex: 3 }} title={s.qualNotes || ''}>
                    {s.qualNotes ? s.qualNotes.slice(0, 80) + (s.qualNotes.length > 80 ? '…' : '') : '—'}
                  </div>
                  <div className="qual-td" style={{ flex: 2 }}>
                    {s.qualReviewer?.name || <span style={{ color: 'var(--tx-muted)' }}>—</span>}
                  </div>
                  <div className="qual-td" style={{ flex: 2 }}>
                    {canEdit ? (
                      <select
                        className="qual-status-select"
                        defaultValue="Needs Review"
                        onChange={async e => {
                          if (e.target.value === 'Needs Review') return;
                          try {
                            await api.patch(`/api/scripts/${s.id}/status`, { status: e.target.value });
                            setScripts(prev => prev.filter(x => x.id !== s.id));
                          } catch (err) {
                            alert(err.response?.data?.error || 'Status update failed');
                          }
                        }}
                      >
                        <option value="Needs Review">Needs Review</option>
                        <option value="Approved">Approved</option>
                        <option value="I/P">I/P</option>
                      </select>
                    ) : (
                      <StatusPill status="Needs Review" />
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ── VIDEOS TAB ── */}
        {tab === 'videos' && (
          <div className="card" style={{ padding: 0 }}>
            <div className="qual-filters">
              {filterSelect(vClientFilter, setVClientFilter,
                clients.map(c => ({ value: c.id, label: c.name })), 'All Projects')}
              <select className="qual-filter-select" value={vTrackFilter} onChange={e => setVTrackFilter(e.target.value)}>
                <option value="">Both Tracks</option>
                <option value="Live">Live</option>
                <option value="AI">AI</option>
              </select>
              {filterSelect(vEditorFilter, setVEditorFilter,
                members.filter(m => {
                  const roles = m.roles || [];
                  return roles.some(r => (r.role || r) === 'Editor');
                }).map(m => ({ value: m.id, label: m.name })), 'All Editors')}
            </div>

            <div className="qual-table">
              <div className="qual-thead">
                <div className="qual-th" style={{ flex: 3 }}>Video / Script</div>
                <div className="qual-th" style={{ flex: 2 }}>Project</div>
                <div className="qual-th" style={{ flex: 1 }}>Track</div>
                <div className="qual-th" style={{ flex: 2 }}>Editor</div>
                <div className="qual-th" style={{ flex: 1 }}>Frame</div>
                <div className="qual-th" style={{ flex: 2 }}>Status</div>
              </div>

              {videosLoading && <div className="qual-empty">Loading…</div>}

              {!videosLoading && videos.length === 0 && (
                <div className="qual-empty">
                  <span style={{ fontSize: 20, marginBottom: 6 }}>✓</span>
                  No videos in qual. Nothing to review.
                </div>
              )}

              {/* D-120: Longform episode videos group under ONE series header (never skit-peers). */}
              {!videosLoading && (() => {
                const out = []; const seen = new Set();
                for (const v of videos) {
                  if (v.episodeNumber != null && v.scriptId) {
                    if (!seen.has(v.scriptId)) {
                      seen.add(v.scriptId);
                      const eps = videos.filter(x => x.scriptId === v.scriptId && x.episodeNumber != null).sort((a, b) => a.episodeNumber - b.episodeNumber);
                      out.push(
                        <div key={`series:${v.scriptId}`} className="qual-row qual-series" data-testid="qual-series" data-episodes={eps.length}>
                          <div className="qual-td" style={{ flex: 3, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8 }}><span>{v.name}</span><span className="format-badge lf" style={{ fontSize: 9 }}>LF</span><span style={{ fontSize: 10, color: 'var(--tx-muted)', fontWeight: 400 }}>{eps.length} episode{eps.length === 1 ? '' : 's'} in Qual</span></div>
                          <div className="qual-td" style={{ flex: 2 }}>{v.client?.name}</div>
                          <div className="qual-td" style={{ flex: 1 }}><TrackBadge track={v.track} /></div>
                          <div className="qual-td" style={{ flex: 2 }} /><div className="qual-td" style={{ flex: 1 }} /><div className="qual-td" style={{ flex: 2 }} />
                        </div>
                      );
                      for (const ep of eps) out.push({ ...ep, __nested: true, name: `Ep ${ep.episodeNumber} — ${ep.episodeTitle || `Episode ${ep.episodeNumber}`}`, __orig: ep });
                    }
                    continue;
                  }
                  out.push(v);
                }
                return out;
              })().map(v => (v && v.$$typeof ? v : (
                <div key={v.id} className={`qual-row${v.__nested ? ' nested' : ''}`} data-testid={v.__nested ? 'qual-episode-row' : undefined}>
                  <div
                    className="qual-td qual-link"
                    style={{ flex: 3 }}
                    onClick={() => setOpenVideo(v.__orig || v)}
                    title={v.name}
                  >
                    {v.name}
                  </div>
                  <div className="qual-td" style={{ flex: 2 }}>{v.client?.name}</div>
                  <div className="qual-td" style={{ flex: 1 }}><TrackBadge track={v.track} /></div>
                  <div className="qual-td" style={{ flex: 2 }}>
                    {v.editor?.name || <span style={{ color: 'var(--tx-muted)' }}>—</span>}
                  </div>
                  <div className="qual-td" style={{ flex: 1 }}>
                    {v.frameLink
                      ? <a href={v.frameLink} target="_blank" rel="noreferrer" className="qual-frame-link" title="Open in Frame.io">⬡</a>
                      : <span style={{ color: 'var(--tx-muted)' }}>—</span>}
                  </div>
                  <div className="qual-td" style={{ flex: 2 }}>
                    {canEdit ? (
                      <select
                        className="qual-status-select"
                        defaultValue="Qual"
                        onChange={async e => {
                          if (e.target.value === 'Qual') return;
                          const endpoint = v.track === 'Live'
                            ? `/api/live-production/${v.id}/status`
                            : `/api/ai-production/${v.id}/status`;
                          try {
                            await api.patch(endpoint, { status: e.target.value, changedBy: user?.id });
                            setVideos(prev => prev.filter(x => x.id !== v.id));
                          } catch (err) {
                            alert(err.response?.data?.error || 'Status update failed');
                          }
                        }}
                      >
                        <option value="Qual">Qual</option>
                        <option value="Approved">Approved</option>
                        <option value="Editing">Editing</option>
                      </select>
                    ) : (
                      <StatusPill status="Qual" />
                    )}
                  </div>
                </div>
              )))}
            </div>
          </div>
        )}

      </div>

      {/* ── Inline panels (rendered outside .body so they overlay everything) ── */}
      {openScriptId && (
        <QualScriptPanel
          scriptId={openScriptId}
          canEdit={canEdit}
          onClose={() => setOpenScriptId(null)}
          onStatusChanged={id => setScripts(prev => prev.filter(s => s.id !== id))}
        />
      )}
      {openVideo && (
        <QualVideoPanel
          video={openVideo}
          canEdit={canEdit}
          userId={user?.id}
          onClose={() => setOpenVideo(null)}
          onStatusChanged={id => setVideos(prev => prev.filter(v => v.id !== id))}
        />
      )}
    </div>
  );
}
